import { useEffect } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';

import { store } from './store.js';
import { useAuthInit } from '@/features/auth/useAuthInit.js';
import { initTheme } from '@/shared/useTheme.js';
import { TooltipProvider } from '@/components/ui/tooltip.js';
import { Toaster } from '@/components/ui/sonner.js';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner.js';

dayjs.locale('ru');

interface ProvidersProps {
  children: React.ReactNode;
}

/**
 * Restores the auth session from httpOnly cookie on app startup.
 * Shows a loading spinner until the refresh attempt completes.
 */
function AuthGate({ children }: { children: React.ReactNode }) {
  const { isLoading } = useAuthInit();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" message="Загружаем приложение..." />
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Theme bootstrap — applies `dark`/`light` class to <html> on mount,
 * mirrors localStorage choice. The pre-script in index.html prevents
 * FOUC for the very first paint.
 */
function ThemeBootstrap({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    initTheme();
  }, []);
  return <>{children}</>;
}

export function Providers({ children }: ProvidersProps) {
  return (
    <ReduxProvider store={store}>
      <ThemeBootstrap>
        <TooltipProvider delayDuration={250}>
          <BrowserRouter>
            <AuthGate>{children}</AuthGate>
          </BrowserRouter>
          <Toaster />
        </TooltipProvider>
      </ThemeBootstrap>
    </ReduxProvider>
  );
}
